import React, { PropsWithChildren, ReactNode } from 'react';
import tw, { TwStyle } from 'twin.macro';
import { Recurrence } from '@teetsh/app/src/containers/Pricing/types';
import { Plan } from '@teetsh/app/src/types/plan';
import _orderBy from 'lodash-es/orderBy';
import { useIsBelowBreakpoint } from '@teetsh/app/src/components/ResponsiveSwitcher/useIsBelowSize';
import { useTranslation } from 'react-i18next';
import EURCurrency from 'components/EURCurrency';
import { PricingFeaturesList } from './PricingFeaturesList';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Tag } from 'components/Tag';
import { PlanTag } from '../PlanTag';

interface Props {
  shownRecurrence: Recurrence;
  paidRecurrence: Recurrence;
  currentPlan: string;
  plans: Plan[];
  isOnOldPricing?: boolean;
  renderControls: (args: { tier: Plan; isCurrentPlan: boolean }) => ReactNode;
}

export const gridCol: Record<2 | 3 | 4 | 5, TwStyle> = {
  2: tw`lg:grid-cols-2 lg:max-w-5xl`,
  3: tw`md:grid-cols-3 md:max-w-5xl`,
  4: tw`lg:grid-cols-4`,
  5: tw`lg:grid-cols-5 lg:max-w-7xl`,
};

export function Plans(props: PropsWithChildren<Props>) {
  const {
    paidRecurrence,
    shownRecurrence,
    currentPlan,
    renderControls,
    plans,
    isOnOldPricing,
  } = props;
  const isMobile = useIsBelowBreakpoint('lg');
  const { t } = useTranslation(['userSettings', 'common']);

  return (
    <div tw="relative z-10">
      <div
        css={[
          tw`space-y-4 md:grid md:gap-5 md:space-y-0`,
          gridCol[plans.length],
        ]}
      >
        {_orderBy(plans, 'priceYearly', [isMobile ? 'desc' : 'asc']).map(
          tier => {
            const isCurrentPlan =
              (currentPlan === 'trial' && tier.id === 'free') ||
              (currentPlan === tier.id && !isOnOldPricing);

            return (
              <div
                key={tier.label}
                tw="flex flex-col rounded-lg shadow-lg overflow-hidden bg-white h-full px-6 py-4 "
              >
                <div tw="flex-grow mb-8">
                  <div tw="mb-2">
                    <div tw="flex gap-x-2 flex-wrap mb-4">
                      <PlanTag plan={tier} />

                      {paidRecurrence === 'YEARLY' &&
                        shownRecurrence === 'MONTHLY' &&
                        tier.priceMonthly > 0 && (
                          <Tag color="gris-bleu-quiet" id="tier-standard">
                            {t(
                              'userSettings:subscription.pricing.saveYearlyBy',
                              {
                                amount:
                                  tier.priceMonthly * 12 - tier.priceYearly,
                              },
                            )}
                          </Tag>
                        )}
                    </div>
                    <div tw="flex items-center gap-x-2">
                      <div tw="display">
                        <EURCurrency
                          amount={
                            (paidRecurrence === 'MONTHLY'
                              ? tier.priceMonthly
                              : shownRecurrence === 'YEARLY'
                              ? tier.priceYearly
                              : tier.priceYearly / 12)!
                          }
                        />
                      </div>
                      {tier.priceMonthly > 0 && (
                        <span tw="ml-1 text-neutral-quiet">
                          /
                          {t(
                            shownRecurrence === 'MONTHLY'
                              ? 'userSettings:subscription.pricing.month'
                              : 'userSettings:subscription.pricing.year',
                          )}
                        </span>
                      )}
                    </div>
                  </div>

                  <p tw="mb-4 text-sm text-neutral-quiet">
                    {tier.marketingDescription}
                  </p>
                  <div tw="flex-1 flex flex-col justify-between bg-neutral-white space-y-6">
                    {tier.marketingFeaturesList ? (
                      <PricingFeaturesList
                        tw="text-neutral-moderate text-base"
                        marketingFeaturesList={tier.marketingFeaturesList}
                      />
                    ) : (
                      // Fallback to old format if marketingFeaturesList not available
                      <div tw="text-neutral-quiet text-sm">
                        {tier.featuresWithDetails.map((feature, i) => (
                          <div key={i} tw="flex items-center space-x-2 mb-2">
                            <CheckIcon tw="h-5 w-5 text-positive-moderate" />
                            <span>{feature.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                {renderControls({
                  isCurrentPlan,
                  tier,
                })}
              </div>
            );
          },
        )}
      </div>
    </div>
  );
}
